import React, { createContext, useContext, useMemo, useState } from 'react';
import { doc, serverTimestamp } from 'firebase/firestore';
import { setDoc } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { milestoneStates, allComplete } from '../domain/onboarding';
import { checklistShows, onboardingCreateDocument, onboardingDismissDocument } from '../domain/home';
import { useDocumentWindow } from './useDocumentWindow';

// Onboarding state is almost entirely derived (see domain/onboarding.js). The
// only thing worth persisting is whether the teacher has dismissed the
// getting-started row, because that is a preference with no data behind it.
//
// There is no `studioNamed` flag: `isStudioNamed` in domain/studio.js answers
// it from the record, and MMS's getting-started stepper is exactly the five
// steps in domain/onboarding.js — Create Your Account, Add a Student, Schedule
// an Event, Create an Invoice, You've got it! — with no "name your studio"
// among them (research/mms-capture/01-empty/01-home-onboarding.jpg).

const OnboardingContext = createContext(null);

const SETTINGS_DOC_ID = 'onboarding';
const SETTINGS_DOC = [COLLECTIONS.settings, SETTINGS_DOC_ID];

// Written at signup so the preference document exists before anything reads it.
// Kept deliberately small: a dismissal flag and a creation stamp.
export async function createOnboardingDoc(uid) {
  if (!db) return;
  await setDoc(doc(db, 'users', uid, ...SETTINGS_DOC), onboardingCreateDocument(serverTimestamp()));
}

// `countsRead`: the students, the schedule and the invoices `counts` are taken
// from have all answered (#629). An unread list counts zero, and zero is a
// milestone not done.
export function OnboardingProvider({ children, counts, countsRead = false }) {
  const { user } = useAuth();

  // ── #207 U5: `users/{uid}/settings/onboarding`, from whichever path serves ──
  //
  // Unlike `settings/studio`, nothing here turns on telling *never heard* from
  // *told it is absent*: both mean "not dismissed", which is what a
  // brand-new studio wants to see. `loading` still distinguishes them, so the
  // row does not flash in and out before the first read lands.
  const read = useDocumentWindow({
    uid: user?.uid,
    collection: COLLECTIONS.settings,
    id: SETTINGS_DOC_ID,
  });
  const loading = read.loading;

  // Covers the render between the click and the read path's own optimism. On
  // the store path the dismissal enters our queue and the
  // overlay folds it into the very next read; on the snapshot path the SDK's
  // own mutation queue does the same. This is never the only thing holding the
  // row shut.
  const [dismissedNow, setDismissedNow] = useState(false);
  const dismissed = dismissedNow || Boolean(read.data?.dismissedChecklist);

  const value = useMemo(() => {
    const milestones = milestoneStates(counts);
    return {
      loading,
      milestones,
      complete: allComplete(counts),
      dismissed,
      // The row hides once dismissed, and also once everything is done — there
      // is nothing left to prompt, and MMS stops showing it at that point too.
      // And it is not drawn before this document and every count have been
      // read (#629): until then it would show a dismissed row, or a studio
      // with four lessons today as one that has never scheduled anything.
      visible: checklistShows({
        read: !loading && countsRead,
        dismissed,
        allDone: allComplete(counts),
      }),
      dismiss: async () => {
        if (!user || !db) return;
        // Optimistic: the row should disappear the instant it is dismissed,
        // offline included. The write queues and syncs whenever it can.
        setDismissedNow(true);
        await setDoc(
          docRef(user.uid, COLLECTIONS.settings, SETTINGS_DOC_ID),
          onboardingDismissDocument(serverTimestamp()),
          { merge: true }
        );
      },
    };
  }, [counts, countsRead, dismissed, loading, user]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within an OnboardingProvider');
  return ctx;
}
